import React, { useState, useCallback, useRef, useMemo } from "react";
import { navigate } from "gatsby-link";
import FormSelect from "./elements/form-select";
import FormInput from "./elements/form-input";
import FormText from "./elements/form-text";
import FormAutocomplete from "./elements/form-autocomplete";
import FormPerson from "./elements/form-person";
import FormPrivacy from "./elements/form-privacy";
import {
  validateSapCode,
  validateProductCode,
  validateDate,
  validateEmail,
  validateTel,
  formatDate,
} from "../utility/formatters";
import { CDN_BASE_URL } from "../utility/constants";
import { getPrivacyData, submitData } from "../utility/forms";
import { GroupProvider } from "../context/group";
import useBabelModels from "../hooks/use-babel-models";
import AltchaWidget from "../utility/altcha";

const serviceBaseUrl = "https://apigateway.candy-group.com/PROD/legacy";

const initialState = {
  type: "",
  productCategory: "",
  model: "",
  serialNumber: "",
  purchaseDate: "",
  firstName: "",
  lastName: "",
  email: "",
  address: "",
  city: "",
  province: "",
  zipCode: "",
  phone: "",
  mobilePhone: "",
  description: "",
};

const changeFormatters = {
  purchaseDate: formatDate,
};

const Form7 = ({ data, labelManagerLabels, siteMetadata }) => {
  const [formData, setFormData] = useState(() => ({
    ...initialState,
    ...getPrivacyData(data),
  }));
  const requestOpts = useMemo(
    () => [
      {
        value: "",
        label: labelManagerLabels.select,
      },
      {
        value: "Check and Care Program",
        label: labelManagerLabels.checkAndCareType,
      },
      {
        value: "Technical Assistance",
        label: labelManagerLabels.checkAndCareAssistance,
      },
    ],
    [labelManagerLabels]
  );
  const matchIndices = useCallback(
    (value, indices) => {
      return indices.includes(requestOpts.findIndex((item) => item.value === value));
    },
    [requestOpts]
  );
  const lineOpts = useMemo(
    () =>
      [
        {
          value: "",
          label: labelManagerLabels.select,
        },
      ].concat(data.lines.selectGroups),
    [data, labelManagerLabels]
  );

  const modelli = useBabelModels(formData.productCategory.split("_")[0]);

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const altchaRef = useRef();
  const onChange = useCallback(
    (e) => {
      const { name } = e.target;
      setFormData((current) => {
        const { name, value, type, checked } = e.target;
        let serialNumber, model;
        if (name === "productCategory" && value !== current.productCategory) {
          serialNumber = "";
          model = "";
        } else if (name === "model" && value) {
          const foundItem = modelli.find((item) => item.productName === value);
          const codeFromItem = foundItem ? foundItem.sapCode || foundItem.key_prod : null;
          if (
            codeFromItem &&
            (!current.serialNumber || !current.serialNumber.startsWith(codeFromItem))
          ) {
            serialNumber = codeFromItem;
          }
        } else if (name === "serialNumber" && value) {
          const key_prod = /^\d+$/.test(value.substring(0, 8)) ? value.substring(0, 8) : null;
          const sapCode = key_prod ? null : value.substring(0, 9);
          const foundItem = key_prod
            ? modelli.find((item) => item.key_prod === key_prod)
            : modelli.find((item) => item.sapCode === sapCode);
          const nameFromItem = foundItem ? foundItem.productName : null;
          if (nameFromItem && (!current.productName || current.productName !== nameFromItem)) {
            model = nameFromItem;
          }
        }
        const formatter = changeFormatters[name];
        const newValue =
          type === "checkbox" ? checked : formatter ? formatter(e, current[name]) : value;
        return {
          ...current,
          [name]: newValue,
          ...(model !== undefined && { model }),
          ...(serialNumber !== undefined && { serialNumber }),
        };
      });
      setFormErrors((current) => (current[name] ? { ...current, [name]: false } : current));
    },
    [modelli]
  );

  const formDataRef = useRef({});
  formDataRef.current = formData;

  const { model, serialNumber } = formData;

  const serialModelMatch = useMemo(() => {
    if (!serialNumber || !model || !modelli) return null;
    const foundItem = modelli.find((item) => item.productName === model);
    const codeFromItem = foundItem ? foundItem.sapCode || foundItem.key_prod : null;
    return codeFromItem &&
      ((foundItem.sapCode && serialNumber.startsWith(foundItem.sapCode)) ||
        serialNumber.startsWith(foundItem.key_prod))
      ? foundItem.sapCode
        ? "SAP"
        : "CODE"
      : null;
  }, [model, serialNumber, modelli]);

  const formInfo = useMemo(
    () => ({
      formData,
      onChange,
      formErrors,
      setFormErrors,
    }),
    [formData, onChange, formErrors, setFormErrors]
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const form = e.currentTarget;
      const formElements = [...form.elements].filter(
        (el) => el.tagName !== "BUTTON" && el.id !== "g-recaptcha-response"
      );
      const missingFields = formElements.filter(
        (field) =>
          field.getAttribute &&
          field.getAttribute("data-required") === "true" &&
          (!field.value || (field.type === "checkbox" && !field.checked))
      );

      const invalidFields = [];
      if (
        form.serialNumber?.value &&
        ((serialModelMatch === "SAP" && !validateSapCode(form.serialNumber.value)) ||
          (serialModelMatch === "CODE" && !validateProductCode(form.serialNumber.value)) ||
          !serialModelMatch)
      ) {
        invalidFields.push("serialNumber");
      }
      if (form.email?.value && !validateEmail(form.email.value)) {
        invalidFields.push("email");
      }
      if (form.purchaseDate?.value && !validateDate(form.purchaseDate.value)) {
        invalidFields.push("purchaseDate");
      }
      if (form.phone?.value && !validateTel(form.phone.value)) {
        invalidFields.push("phone");
      }
      if (form.mobilePhone?.value && !validateTel(form.mobilePhone.value)) {
        invalidFields.push("mobilePhone");
      }

      if (missingFields.length || invalidFields.length) {
        const missingErrs = missingFields.reduce(
          (res, field) => ({
            ...res,
            [field.name]: labelManagerLabels.requiredFields,
          }),
          {}
        );
        const errors = invalidFields.reduce(
          (res, field) => ({ ...res, [field]: true }),
          missingErrs
        );
        setFormErrors((current) => ({ ...current, ...errors }));
        console.warn("Cannot submit: ", errors);
        const firstElError = formElements.find(
          (el) =>
            missingFields.some((field) => el.name === field.name) || invalidFields.includes(el.name)
        );
        var headerOffset =
          document.getElementsByClassName("header")[0].getBoundingClientRect().height + 50;
        var elementPosition = firstElError.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          behavior: "smooth",
          top: offsetPosition,
        });

        return;
      }

      setLoading(true);
      setServerError(false);

      const modelName = modelli.find(
        (item) => item.productName === formDataRef.current.model
      )?.modelName;

      try {
        const response = await fetch(
          `${serviceBaseUrl}/getproductslines/${siteMetadata.babelPrefix}/haier`
        );
        const data = await response.json();
        const prodCode = data?.GetProductsLinesResult?.find(
          (line) => line.id === Number(form.productCategory.value.split("_")[0])
        )?.products?.find((prod) => prod.name === modelName)?.id;
        formDataRef.current.serialNumber = prodCode ? prodCode + "00000000" : "";
        if (!response.ok) {
          throw new Error(`Error ${response.status} ${response.statusText}`);
        }
      } catch (err) {
        console.log("Error loading modelli: ", err);
      }

      const formPayload = {
        ...formDataRef.current,
        model: modelName,
      };

      try {
        const result = await submitData({ ...formPayload }, captcha, data.privacyText, "form7");
        console.log("result: ", result);
        if (data.retUrl) {
          const slug = data.retUrl;
          navigate(slug.startsWith("/") ? slug : `/${slug}`);
        } else {
          navigate("/");
        }
      } catch (err) {
        setServerError(true);
        console.error("Error submitting: ", err);
        altchaRef.current?.reset();
        setCaptcha(null);
      }

      setLoading(false);
    },
    [
      data.privacyText,
      data.retUrl,
      labelManagerLabels,
      siteMetadata.babelPrefix,
      serialModelMatch,
      modelli,
      captcha,
    ]
  );

  return (
    <section className="section form">
      <div className="container">
        <div className="row">
          <div className="col-12 px-0">
            <div className="form__container">
              <form className="form" onSubmit={onSubmit}>
                <GroupProvider data={formInfo}>
                  <FormSelect
                    id="request"
                    field="type"
                    label={labelManagerLabels.formRequest}
                    options={requestOpts}
                    isRequired={true}
                  />
                  {formData.type && matchIndices(formData.type, [0, 1]) && (
                    <>
                      <FormSelect
                        id="line"
                        field="productCategory"
                        label={labelManagerLabels.formProductLine}
                        options={lineOpts}
                        isRequired={true}
                      />
                      {formData.productCategory &&
                        (!modelli ? (
                          <div className="py-4 d-flex align-items-center justify-content-center">
                            <div className="spinner-border"></div>
                          </div>
                        ) : (
                          <>
                            <FormAutocomplete
                              id="productModel"
                              field="model"
                              maxLength={20}
                              options={modelli ? modelli.map((item) => item.productName) : []}
                              isRequired={true}
                              label={labelManagerLabels.formModel}
                              autoSelect={false}
                            />

                            {serialModelMatch ? (
                              <FormInput
                                id="productCode"
                                field="serialNumber"
                                maxLength={serialModelMatch === "SAP" ? 20 : 16}
                                label={`${
                                  labelManagerLabels.formProductCode ||
                                  labelManagerLabels.serialNumber
                                } *`}
                                isRequired={true}
                                validate={
                                  serialModelMatch === "SAP" ? validateSapCode : validateProductCode
                                }
                                formatWarning={
                                  serialModelMatch === "SAP"
                                    ? labelManagerLabels.valid20Letters
                                    : labelManagerLabels.valid16Numbers
                                    ? labelManagerLabels.valid16Numbers
                                    : labelManagerLabels.serialNumberValidation
                                }
                              />
                            ) : (
                              <FormAutocomplete
                                id="productCode"
                                field="serialNumber"
                                maxLength={20}
                                options={
                                  modelli
                                    ? modelli.map((item) => item.sapCode || item.key_prod)
                                    : []
                                }
                                isRequired={true}
                                label={labelManagerLabels.formProductCode}
                                autoSelect={false}
                                validate={() => false}
                                formatWarning={
                                  /^\d{8}/.test(serialNumber)
                                    ? labelManagerLabels.valid16Numbers
                                    : labelManagerLabels.valid20Letters
                                }
                              />
                            )}

                            <div className="form__product d-flex flex-wrap align-items-start">
                              <p className="form__label">{labelManagerLabels.bookletFindImage}</p>
                              <img
                                alt={labelManagerLabels.formProductLine}
                                src={`${CDN_BASE_URL}/booklet/libretto-${
                                  formData.productCategory.split("_")[0]
                                }.png`}
                              />
                              <img
                                alt={labelManagerLabels.howReadProductCode}
                                src={`${CDN_BASE_URL}/booklet/libretto-label.png`}
                              />
                            </div>
                          </>
                        ))}

                      {/*                     <FormInput id="productModel" field="model" maxLength={20}
                          placeholder={`${labelManagerLabels.formModel} *`} isRequired={true} /> */}

                      <FormInput
                        id="purchaseDate"
                        field="purchaseDate"
                        maxLength={10}
                        placeholder={`${
                          labelManagerLabels.formPurchaseDate + " " + labelManagerLabels.dateFormat
                        } *`}
                        isRequired={true}
                        validate={validateDate}
                        formatWarning={labelManagerLabels.validDate}
                      />
                      <FormPerson labelManagerLabels={labelManagerLabels} mobileRequired={true} />
                      <FormText
                        id="description"
                        field="description"
                        maxLength={32000}
                        placeholder={`${labelManagerLabels.formComments} *`}
                        isRequired={true}
                        rows={10}
                      />
                      <FormPrivacy
                        data={data.privacyText}
                        labelManagerLabels={labelManagerLabels}
                      />
                      <div className="my-5">
                        <AltchaWidget ref={altchaRef} onChange={setCaptcha} />
                      </div>
                      <div className="text-center">
                        <button className="btn-link" disabled={!captcha || loading}>
                          {labelManagerLabels.send}
                        </button>
                        {serverError && (
                          <p className="mt-2 form__service-not-available">
                            {labelManagerLabels.serviceNotAvailable}
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {formData.type && matchIndices(formData.type, [2]) && (
                    <div
                      className="form__block p-3 pb-4"
                      dangerouslySetInnerHTML={{
                        __html: labelManagerLabels.checkAndCareAssistanceText,
                      }}
                    ></div>
                  )}

                  <div className="form__info mt-4">* {labelManagerLabels.requiredFields}</div>
                </GroupProvider>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Form7;
